import { useState, type ReactNode } from 'react';
import { useQueryClient } from 'react-query';
import {
  ShieldCheck,
  KeyRound,
  Building2,
  CalendarDays,
  Monitor,
  Users,
  Sparkles,
  AlertTriangle,
  Check,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../services/api';
import { Button, Card, Input, ListPageSkeleton, MaskedValue, OutcomeBadge, Timestamp } from '@/components/ds-zta';
import type { LicenseState } from '@/lib/license';
import { LICENSE_QUERY_KEY, useLicenseState } from '../hooks/useLicenseState';
import type { OutcomeTone } from '@/lib/tone';

/* ─── Types mirroring pkg/api/license.go ─────────────── */

// Moved to lib/license.ts so the shell's licence banner (F25) reads the same
// shape and the same query; re-exported for existing importers.
export type { LicenseFeature, LicenseState } from '@/lib/license';

/* ─── Status pill ────────────────────────────────────── */

// The licence words mean something specific here, so each carries its tone
// explicitly: an active licence is healthy (resolved); an expired or invalid
// one suspends the product, a risk (high); an unverified one is held on the
// last known state (contained); an installation never activated is inactive
// (closed).
const STATUS_STYLES: Record<LicenseState['status'], { label: string; tone: OutcomeTone }> = {
  active: { label: 'Active', tone: 'resolved' },
  expired: { label: 'Expired', tone: 'high' },
  invalid: { label: 'Invalid', tone: 'high' },
  unlicensed: { label: 'Not activated', tone: 'closed' },
  // The licence service could not be reached; the last known state still stands.
  unavailable: { label: 'Unverified', tone: 'contained' },
};

function StatusPill({ status }: { status: LicenseState['status'] }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.unlicensed;
  return (
    <OutcomeBadge shape="chip" value={status} tone={s.tone}>
      {s.label}
    </OutcomeBadge>
  );
}

/* ─── Usage meter ────────────────────────────────────── */

function UsageMeter({
  icon: Icon,
  label,
  used,
  limit,
}: {
  icon: typeof Users;
  label: string;
  used: number;
  limit: number | null;
}) {
  // A null limit is unlimited. Showing 0 there would read as "none allowed",
  // which is the opposite of the truth.
  const unlimited = limit === null || limit === undefined;
  // A count the server did not send is no value ("—"), not a bare "/ ∞" (F47).
  const known = typeof used === 'number' && Number.isFinite(used);
  const pct = unlimited || limit === 0 || !known ? 0 : Math.min(100, Math.round((used / limit) * 100));
  // Colour is a warning, not decoration: it only changes as the limit nears.
  const bar = pct >= 90 ? 'bg-[var(--danger-fill)]' : pct >= 75 ? 'bg-[var(--d2d-warning)]' : 'bg-[var(--action-primary)]';

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="p-1.5 rounded-md bg-[var(--ds-color-bg-subtle)]">
            <Icon className="h-4 w-4 text-[var(--d2d-text-primary)]" />
          </span>
          <span className="text-sm font-medium text-[var(--d2d-text-secondary)]">{label}</span>
        </div>
        <div className="text-sm tabular-nums">
          <span className="text-lg font-medium text-[var(--d2d-text-primary)]">{known ? used : '—'}</span>
          <span className="text-[var(--d2d-text-muted)]">{unlimited ? (known ? ' in use' : '') : ` / ${limit}`}</span>
        </div>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[var(--ds-color-bg-subtle)] overflow-hidden">
        <div className={clsx('h-full rounded-full transition-all', bar)} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-[var(--d2d-text-muted)]">
        {unlimited ? 'Unlimited' : `${pct}% used`}
      </p>
    </Card>
  );
}

/* ─── Key display ────────────────────────────────────── */

// The licence key is a secret (decision D4 = A): masked by default, revealed
// per view by an admin, and copied as shown.
function KeyField({ value }: { value: string }) {
  return (
    <div className="rounded-md bg-[var(--d2d-bg-primary)] border border-[var(--d2d-border)] px-3 py-1.5 text-sm text-[var(--d2d-text-primary)]">
      <MaskedValue value={value} kind="secret" label="license key" className="w-full justify-between" />
    </div>
  );
}

/* ─── License tab ────────────────────────────────────── */

export default function LicenseTab() {
  // The same query the shell's licence banner reads; refresh and activate
  // write their answer into it, so the banner follows at once.
  const queryClient = useQueryClient();
  const licenseQuery = useLicenseState();
  const state: LicenseState | null = licenseQuery.data ?? null;
  const setState = (next: LicenseState) => queryClient.setQueryData(LICENSE_QUERY_KEY, next);
  const loading = licenseQuery.isLoading;
  const [refreshing, setRefreshing] = useState(false);
  const [activating, setActivating] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [actionError, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const loadError = licenseQuery.error as { response?: { data?: { error?: string } } } | null;
  const error = actionError ?? (loadError ? loadError.response?.data?.error ?? 'Could not read the license state.' : null);
  const load = () => {
    setError(null);
    return licenseQuery.refetch();
  };

  const refresh = async () => {
    setRefreshing(true);
    setNotice(null);
    try {
      const { data } = await api.post<LicenseState>('/license/refresh');
      setState(data);
      setError(null);
      setNotice('License re-checked.');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not reach the license service.');
    } finally {
      setRefreshing(false);
    }
  };

  const activate = async () => {
    const key = keyInput.trim();
    if (!key) return;
    setActivating(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post<LicenseState>('/license/activate', { activationKey: key });
      setState(data);
      setKeyInput('');
      setNotice('License activated.');
    } catch (e: any) {
      // The message from the licence service is the useful part — "key already
      // used", "unknown key" — so it is surfaced rather than replaced.
      setError(e?.response?.data?.error ?? 'Activation failed.');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <ListPageSkeleton aria-label="Loading license" title filterCount={0} rowCount={6} />
    );
  }

  const s = state;
  const expired = s ? !s.valid : true;

  return (
    <div className="space-y-6">
      {/* Heading */}
      <div className="flex items-start gap-3">
        <span className="p-2 rounded-lg bg-[var(--action-primary)]">
          <ShieldCheck className="h-5 w-5 text-[var(--action-primary-text)]" />
        </span>
        <div>
          <h3 className="text-lg font-medium text-[var(--d2d-text-primary)]">License Management</h3>
          <p className="text-sm text-[var(--d2d-text-muted)]">
            {s?.can_activate
              ? 'Activate this installation and view its subscription.'
              : 'Your subscription is managed automatically and refreshed hourly.'}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--zta-tint-danger-border)] bg-[var(--zta-tint-danger)] px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-[var(--danger-text)] mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-[var(--danger-text)]">{error}</p>
          {!state && (
            <Button variant="secondary" size="sm" onClick={() => load()}>
              Retry
            </Button>
          )}
        </div>
      )}
      {notice && !error && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--zta-tint-success-border)] bg-[var(--zta-tint-success)] px-4 py-3">
          <Check className="h-4 w-4 text-[var(--d2d-success)] mt-0.5 flex-shrink-0" />
          <p className="text-sm text-[var(--d2d-success)]">{notice}</p>
        </div>
      )}

      {/* Suspension banner — the most important thing on the page when it
          applies, because it explains why the rest of the product has stopped
          responding. This page and signing in are the only things that keep
          working, so it is the one place the reason can be shown. */}
      {expired && (
        <div className="flex items-start gap-2 rounded-lg border border-[var(--zta-tint-warning-border)] bg-[var(--zta-tint-warning)] px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-[var(--d2d-warning)] mt-0.5 flex-shrink-0" />
          <div className="text-sm text-[var(--d2d-warning)]">
            <p className="font-medium">Access to this product is suspended.</p>
            <p className="mt-0.5">
              {s?.status === 'expired'
                ? 'Your subscription has expired. Resources, sessions and settings are unavailable until it is renewed. Your data is untouched.'
                : s?.status === 'unlicensed'
                  ? 'This installation has not been licensed yet. Resources and sessions are unavailable until a license is active.'
                  : 'The license could not be verified. Resources and sessions are unavailable until it can be confirmed. Your data is untouched.'}
            </p>
          </div>
        </div>
      )}

      <div className={clsx('grid gap-4', s?.can_activate ? 'lg:grid-cols-2' : 'grid-cols-1')}>
        {/* Activation — on-prem only */}
        {s?.can_activate && (
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="p-1.5 rounded-md bg-[var(--ds-color-bg-subtle)]">
                <KeyRound className="h-4 w-4 text-[var(--d2d-text-primary)]" />
              </span>
              <h4 className="font-medium text-[var(--d2d-text-primary)]">Activate License</h4>
            </div>
            <Input
              id="license-key"
              label="License Key"
              className="[&_input]:font-mono"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && activate()}
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
            />
            <Button className="mt-4 w-full" onClick={activate} disabled={activating || !keyInput.trim()} loading={activating}>
              {activating ? 'Activating…' : 'Activate License'}
            </Button>
            <p className="mt-3 text-xs text-[var(--d2d-text-muted)]">
              The key is exchanged once for a signed license. After that it is re-checked locally every hour, so no
              outbound connection is needed to keep working.
            </p>
          </Card>
        )}

        {/* Current licence */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-md bg-[var(--zta-tint-success)]">
                <Sparkles className="h-4 w-4 text-[var(--d2d-success)]" />
              </span>
              <h4 className="font-medium text-[var(--d2d-text-primary)]">Current License</h4>
            </div>
            <div className="flex items-center gap-3">
              <StatusPill status={s?.status ?? 'unlicensed'} />
              <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing} loading={refreshing}>
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>

          {!s || s.status === 'unlicensed' ? (
            <div className="space-y-2">
              <p className="text-sm text-[var(--d2d-text-muted)]">
                No license is active on this installation.
              </p>
              {/*
                The licence service explains WHY, and that explanation is the only
                thing that distinguishes "nobody has activated this yet" from "the
                identifier we sent does not match any subscription". Without it
                both look identical and the difference is only findable by reading
                server logs.
              */}
              {s?.message && (
                <p className="text-sm text-[var(--d2d-warning)]">
                  <span className="font-medium">License service:</span> {s.message}
                </p>
              )}
              {s?.message && s.mode === 'saas' && (
                <p className="text-xs text-[var(--d2d-text-muted)]">
                  A SaaS subscription is matched on this workspace's client ID. If this
                  says the subdomain or product was not found, the workspace is not
                  registered with the license service for this product.
                </p>
              )}
            </div>
          ) : (
            <dl className="space-y-3 text-sm">
              {s.company && (
                <Row icon={Building2} label="Company" value={s.company} />
              )}
              <Row icon={ShieldCheck} label="License type" value={s.plan_name || s.plan_code || '—'} />
              <Row
                icon={CalendarDays}
                label="Expires"
                value={<Timestamp value={s.end_date} noSeconds />}
                sub={
                  s.end_date
                    ? s.valid
                      ? `${s.days_remaining} day${s.days_remaining === 1 ? '' : 's'} remaining`
                      : 'Expired'
                    : undefined
                }
              />
              {s.license_key && (
                <div className="pt-2 border-t border-[var(--d2d-border)]">
                  <dt className="text-label uppercase text-[var(--d2d-text-muted)] mb-1.5">
                    License Key
                  </dt>
                  <dd>
                    <KeyField value={s.license_key} />
                  </dd>
                </div>
              )}
              {s.last_checked_at && (
                <p className="pt-1 text-xs text-[var(--d2d-text-muted)]">
                  Last checked <Timestamp value={s.last_checked_at} />
                </p>
              )}
            </dl>
          )}
        </Card>
      </div>

      {/* Usage */}
      {s && s.status !== 'unlicensed' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <UsageMeter icon={Monitor} label="Agent Limit" used={s.agent_count} limit={s.agent_limit} />
          <UsageMeter icon={Users} label="User Limit" used={s.user_count} limit={s.user_limit} />
        </div>
      )}

      {/* Features. Deliberately hidden while the licence is invalid: an expired
          subscription entitles nothing, and listing features would suggest
          otherwise. */}
      {s && s.valid && s.features.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-md bg-[var(--zta-tint-warning)]">
                <Sparkles className="h-4 w-4 text-[var(--d2d-warning)]" />
              </span>
              <h4 className="font-medium text-[var(--d2d-text-primary)]">Available Features</h4>
            </div>
            <span className="rounded-full bg-[var(--ds-color-bg-subtle)] px-2.5 py-1 text-xs text-[var(--d2d-text-secondary)]">
              {s.features.length} feature{s.features.length === 1 ? '' : 's'}
            </span>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {s.features.map((f) => (
              <li
                key={f.code}
                className="flex items-center gap-2 rounded-md border border-[var(--d2d-border)] px-3 py-2"
              >
                <Check
                  className={clsx(
                    'h-4 w-4 flex-shrink-0',
                    f.enabled ? 'text-[var(--d2d-success)]' : 'text-[var(--d2d-text-muted)]'
                  )}
                />
                <span
                  className={clsx(
                    'text-sm truncate',
                    f.enabled ? 'text-[var(--d2d-text-secondary)]' : 'text-[var(--d2d-text-muted)] line-through'
                  )}
                  title={f.name || f.code}
                >
                  {f.name || f.code}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Building2;
  label: string;
  value: ReactNode;
  sub?: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-[var(--d2d-text-muted)] mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <dt className="text-label uppercase text-[var(--d2d-text-muted)]">{label}</dt>
        <dd className="font-medium text-[var(--d2d-text-primary)] truncate">{value}</dd>
        {sub && <p className="text-xs text-[var(--d2d-text-muted)] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}
